import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useEffect, useState } from "react";
import { SessionRecovery } from "./session-recovery";
import { Button } from "@/components/ui/button";
import { AlertCircle, LogOut, RefreshCw } from "lucide-react";
import { clearCorruptedSession } from "@/lib/utils/auth-utils";
import { supabase } from "@/lib/supabase";

export function AuthLoading() {
  const [showRecovery, setShowRecovery] = useState(false);
  const [showCriticalRecovery, setShowCriticalRecovery] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    // Show initial recovery option after 3 seconds
    const quickTimer = setTimeout(() => {
      setShowRecovery(true);
    }, 3000);

    // Show critical recovery options after 30 seconds
    const criticalTimer = setTimeout(() => {
      setShowCriticalRecovery(true);
      console.error("[AuthLoading] Session loading timeout - showing critical recovery options");
    }, 30000);

    // Track elapsed time for user feedback
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearTimeout(quickTimer);
      clearTimeout(criticalTimer);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      clearCorruptedSession();
      window.location.href = '/signin';
    } catch (error) {
      console.error("Error during logout:", error);
      // Force clear and redirect even if logout fails
      clearCorruptedSession();
      window.location.href = '/signin';
    }
  };

  const handleReset = () => {
    clearCorruptedSession();
    window.location.reload();
  };

  // Show critical recovery UI after 30 seconds
  if (showCriticalRecovery) {
    return (
      <div className="fixed inset-0 bg-card flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border rounded-lg shadow-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div className="space-y-2 flex-1">
              <h3 className="text-lg font-semibold">Session Loading Timeout</h3>
              <p className="text-sm text-on-dark-muted">
                We're having trouble loading your session. This might be due to network issues, 
                browser storage problems, or an expired session.
              </p>
              <p className="text-xs text-on-dark-muted">
                Waiting for {elapsedTime} seconds...
              </p>
            </div>
          </div>
          
          <div className="space-y-2 pt-2">
            <Button 
              onClick={handleReset} 
              className="w-full"
              variant="default"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            
            <Button 
              onClick={handleLogout}
              className="w-full"
              variant="outline"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out & Start Fresh
            </Button>
            
            <p className="text-xs text-center text-on-dark-muted pt-2">
              If the problem persists, try clearing your browser cache or using a different browser.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-transparent flex flex-col items-center justify-center">
      <LoadingSpinner text="Loading your session..." size="lg" />
      {showRecovery && <SessionRecovery />}
    </div>
  );
}
